import React, { useState, useCallback, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Expand } from 'lucide-react';
import { Badge } from './ui/badge';
import Lightbox from './Lightbox';

/**
 * Lightweight product image carousel.
 * - Uses product.images (array) when present, falls back to product.image
 * - Keyboard arrow nav, thumbnail strip, image counter, smooth fade
 */
const ProductImageCarousel = ({ product }) => {
  const images = (product.images && product.images.length > 0)
    ? product.images
    : [product.image];
  const [activeIdx, setActiveIdx] = useState(0);
  const [prevSlug, setPrevSlug] = useState(product.slug);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // React-blessed pattern for "reset state when a prop changes" — avoids the
  // double-render set-state-in-effect would cause.
  // See: https://react.dev/reference/react/useState#storing-information-from-previous-renders
  if (prevSlug !== product.slug) {
    setPrevSlug(product.slug);
    setActiveIdx(0);
  }

  const go = useCallback(
    (delta) => setActiveIdx((i) => (i + delta + images.length) % images.length),
    [images.length]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'ArrowRight') go(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go]);

  return (
    <div data-testid="product-image-carousel">
      <div className="relative aspect-square rounded-2xl overflow-hidden bg-white shadow-lg group">
        <button
          type="button"
          onClick={() => setLightboxOpen(true)}
          aria-label={`Open ${product.name} preview at full size`}
          data-testid="carousel-open-lightbox"
          className="absolute inset-0 z-[1] focus:outline-none focus:ring-4 focus:ring-teal-300/50 cursor-zoom-in"
        >
          <span className="sr-only">Open full-size preview</span>
        </button>
        <img
          src={images[activeIdx]}
          alt={`${product.name} — view ${activeIdx + 1} of ${images.length}`}
          className="w-full h-full object-contain transition-opacity duration-300 pointer-events-none"
          onError={(e) => {
            e.target.src = 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=800';
          }}
        />

        {/* Expand affordance — visible on hover */}
        <div
          className="absolute top-4 left-4 z-[2] h-9 w-9 rounded-full bg-white/95 shadow-md flex items-center justify-center text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
          aria-hidden="true"
        >
          <Expand className="h-4 w-4" />
        </div>

        {product.badge && (
          <Badge className="absolute top-4 right-4 z-[2] bg-teal-600 text-white text-xs font-semibold uppercase tracking-wide px-3 py-1 pointer-events-none">
            {product.badge}
          </Badge>
        )}

        {images.length > 1 && (
          <>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); go(-1); }}
              aria-label="Previous image"
              data-testid="carousel-prev"
              className="absolute left-3 top-1/2 -translate-y-1/2 z-[3] h-10 w-10 rounded-full bg-white/95 hover:bg-white shadow-md flex items-center justify-center text-slate-700 transition-all opacity-0 group-hover:opacity-100"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); go(1); }}
              aria-label="Next image"
              data-testid="carousel-next"
              className="absolute right-3 top-1/2 -translate-y-1/2 z-[3] h-10 w-10 rounded-full bg-white/95 hover:bg-white shadow-md flex items-center justify-center text-slate-700 transition-all opacity-0 group-hover:opacity-100"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
            <div
              className="absolute bottom-3 left-1/2 -translate-x-1/2 z-[3] bg-black/55 text-white text-xs px-2.5 py-1 rounded-full font-medium pointer-events-none"
              aria-live="polite"
            >
              {activeIdx + 1} / {images.length}
            </div>
          </>
        )}
      </div>

      {images.length > 1 && (
        <div
          className="mt-3 grid gap-2"
          style={{ gridTemplateColumns: `repeat(${Math.min(images.length, 6)}, minmax(0, 1fr))` }}
          data-testid="carousel-thumbnails"
        >
          {images.map((src, i) => (
            <button
              key={src}
              type="button"
              onClick={() => setActiveIdx(i)}
              aria-label={`View image ${i + 1}`}
              data-testid={`carousel-thumb-${i}`}
              className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all bg-white ${
                i === activeIdx
                  ? 'border-teal-600 ring-2 ring-teal-200'
                  : 'border-transparent hover:border-slate-300'
              }`}
            >
              <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
            </button>
          ))}
        </div>
      )}

      <Lightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        images={images}
        startIndex={activeIdx}
        title={product.name}
      />
    </div>
  );
};

export default ProductImageCarousel;
